import React from "react";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import {
    usePrintReady,
    DocOpening,
    DocSection,
    SubHead,
    P,
    T,
    Steps,
    List,
    MarginNote,
    MailLink,
    DocTable,
    Colophon,
} from "./DocParts";

const FEATURES = [
    {
        name: "Chat",
        items: [
            "Upload docs (PDF, DOCX, XLSX, max 50MB)",
            "Paste YouTube URLs for video discussion",
            "Voice input (microphone icon)",
            "Text-to-speech responses",
            "Search suggestions for housing topics",
        ],
    },
    {
        name: "Studio",
        items: [
            "Creative Studio: chat, edit and iterate",
            "1-4 variants per generation",
            "10 aspect ratios (1:1, 16:9, 9:16, 4:3, 3:4 and more)",
            "1K / 2K / 4K resolution",
            "Reference images and document context",
        ],
    },
    {
        name: "Analyzer",
        items: [
            "Upload inspection reports or enter data manually",
            "Auto-extract property details",
            "JPRO Index score (0-100)",
            "Market data lookup",
            "Export reports (PDF/Word)",
        ],
    },
    {
        name: "Documents",
        items: [
            "View all uploaded and generated files",
            "Filter by type (PDFs, Images, Generated)",
            "Search by title",
            "Download, preview, or delete",
            "50MB max per file",
        ],
    },
];

// Score bands, with the share of the 0-100 scale each one covers.
const SCORE_BANDS = [
    { range: "80-100", name: "Excellent", from: 80, to: 100, body: "Strong investment opportunity with high profitability potential" },
    { range: "60-79", name: "Good", from: 60, to: 80, body: "Solid investment with manageable risk factors" },
    { range: "40-59", name: "Moderate", from: 40, to: 60, body: "Requires improvements, consider carefully before investing" },
    { range: "Below 40", name: "High Risk", from: 0, to: 40, body: "Significant challenges, substantial improvements needed" },
];

const TROUBLESHOOTING = [
    {
        title: "Chat/File Upload Issues",
        items: [
            "Chat not loading → Refresh page or clear browser cache",
            "File won't upload → Check size (under 50MB) and format",
            "Session timeout → Log in again to continue",
        ],
    },
    {
        title: "Analyzer Problems",
        items: [
            "Extraction failing → Document must have readable text (not scanned images)",
            "Missing market data → Verify property address is complete and valid",
            "Report won't export → Check browser download settings, disable popup blockers",
        ],
    },
    {
        title: "Image Generation Issues",
        items: [
            "Images not generating → Rephrase prompt (may violate content policies)",
            "Slow generation → High traffic periods cause delays",
            "Images not displaying → Clear browser cache, disable ad blockers",
        ],
    },
    {
        title: "Login & Account",
        items: [
            'Forgot password → Use "Forgot Password" link on login page',
            "OAuth login fails → Ensure browser allows popups",
            "Session expired → Normal after 24 hours, just log in again",
        ],
    },
];

const BEST_PRACTICES = [
    { name: "Chat", items: ["Be specific in questions", "Upload relevant docs first", "Ask follow-up questions", "Reference uploaded files"] },
    { name: "Studio", items: ["Include style keywords", "Specify lighting details", "Describe composition", "Add colors, textures, mood"] },
    { name: "Analyzer", items: ["Provide complete address", "Use clear inspection reports", "Verify extracted data", "Compare multiple properties"] },
    { name: "Documents", items: ["Use clear file names", "Organize by project", "Delete old files regularly", "Preview before sharing"] },
];

// Two-column ruled grid of named lists (no cards, no icons).
function NamedLists({ groups }) {
    return (
        <div className="grid max-w-4xl gap-x-12 sm:grid-cols-2">
            {groups.map(({ name, items }) => (
                <div key={name} className="border-t border-ink/15 pb-8 pt-5 dark:border-paper/15">
                    <h3 className="mb-4 font-news text-[22px] leading-tight text-ink dark:text-paper print:text-black">{name}</h3>
                    <List items={items} className="text-[15.5px]" />
                </div>
            ))}
        </div>
    );
}

function QuickStart() {
    usePrintReady();

    return (
        <StandalonePageLayout>
            <DocOpening
                appendix="A"
                label="Quick start"
                title="Quick Start Guide"
                intro="Essential JPRO reference"
            />

            <DocSection
                number="A.1"
                label="Setup"
                title="5-Minute Setup"
                aside={<MarginNote>Four steps from sign-up to your first conversation.</MarginNote>}
            >
                <Steps
                    items={[
                        <><T>Sign up:</T> Use email or Google/Microsoft OAuth</>,
                        <><T>Complete profile:</T> Add name, phone, location (optional: avatar)</>,
                        <><T>Choose theme:</T> Settings → Toggle Dark/Light mode</>,
                        <><T>Start exploring:</T> Dashboard → Chat, Studio, or Analyzer</>,
                    ]}
                />
            </DocSection>

            <DocSection number="A.2" label="Features" title="Key Features at a Glance">
                <NamedLists groups={FEATURES} />
            </DocSection>

            <DocSection
                number="A.3"
                label="Limits"
                title="File Formats & Limits"
                aside={<MarginNote>50MB is the ceiling for every upload.</MarginNote>}
            >
                <DocTable
                    head={["Type", "Formats", "Max Size", "Usage"]}
                    rows={[
                        ["Documents", "PDF, DOCX, XLSX", "50MB", "Chat, Analyzer"],
                        ["Images", "PNG, JPG", "50MB", "Chat, Profile"],
                        ["Generated", "PNG", "N/A", "Studio output"],
                    ]}
                />
            </DocSection>

            <DocSection number="A.4" label="Scoring" title="JPRO Index Score Guide">
                <P>Property profitability scoring (0-100):</P>
                <div className="max-w-3xl border-b border-ink/15 dark:border-paper/15">
                    {SCORE_BANDS.map((band) => (
                        <div
                            key={band.range}
                            className="grid gap-2 border-t border-ink/15 py-5 sm:grid-cols-[150px_1fr] sm:gap-6 dark:border-paper/15"
                        >
                            <div>
                                <p className="font-plex-mono text-[15px] text-ink dark:text-paper print:text-black">{band.range}</p>
                                {/* Where the band sits on the 0-100 scale */}
                                <div aria-hidden="true" className="relative mt-2 h-px w-full max-w-[120px] bg-ink/15 dark:bg-paper/15">
                                    <div
                                        className="absolute -top-px h-[3px] bg-ink dark:bg-paper print:bg-black"
                                        style={{ left: `${band.from}%`, width: `${band.to - band.from}%` }}
                                    />
                                </div>
                            </div>
                            <div>
                                <p className="font-news text-[21px] leading-tight text-ink dark:text-paper print:text-black">{band.name}</p>
                                <p className="mt-1 text-[15.5px]">{band.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </DocSection>

            <DocSection number="A.5" label="Troubleshooting" title="Quick Troubleshooting">
                {TROUBLESHOOTING.map(({ title, items }) => (
                    <React.Fragment key={title}>
                        <SubHead>{title}</SubHead>
                        <List items={items} className="text-[15.5px]" />
                    </React.Fragment>
                ))}
            </DocSection>

            <DocSection number="A.6" label="Practice" title="Best Practices">
                <NamedLists groups={BEST_PRACTICES} />
            </DocSection>

            <DocSection number="A.7" label="Support" title="Need Help?">
                <SubHead>Contact Support</SubHead>
                <List
                    items={[
                        <><T>Email:</T> <MailLink /></>,
                        <><T>Response time:</T> Within 2 business days</>,
                        "Include screenshots and error messages for faster resolution",
                    ]}
                />
            </DocSection>

            <Colophon>© 2025 Zeffert & Associates. All rights reserved.</Colophon>
        </StandalonePageLayout>
    );
}

export default QuickStart;
